import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Typography from "@material-ui/core/Typography";
import DashView from "../../customViews/Dashboard/DashView";
import { useGlobal } from "../../GlobalContext.js";
import { search } from "../../services/queryService.js";

import SearchBar from "customComponents/autocompleteSearch";
import { useTranslation } from "react-i18next";

/**
 * Pantalla principal de búsqueda y visualización del dashboard.
 *
 * Objetivos:
 * - preservar la funcionalidad nueva de main
 * - mantener las mejoras responsive
 * - evitar márgenes rígidos y layouts frágiles
 * - en móvil / pantallas pequeñas, hacer autoscroll suave cuando
 *   ya se ha cargado un análisis para que el usuario vea claramente
 *   que hay contenido nuevo debajo del disclaimer
 */
const useStyles = makeStyles((theme) => ({
  cardTitle: {
    fontWeight: 500,
    fontSize: "1.5rem",

    [theme.breakpoints.down("sm")]: {
      fontSize: "1.25rem",
    },
  },

  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2.5),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },

  searchWrapper: {
    width: "100%",
    maxWidth: 760,
    marginBottom: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      marginBottom: theme.spacing(1.5),
    },
  },

  gridContainer: {
    marginBottom: theme.spacing(3),

    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1),
    },
  },

  disclaimer: {
    width: "100%",
    maxWidth: 980,
    margin: "0 auto",
    marginTop: theme.spacing(1),
    color: "gray",
    fontSize: "14px",
    lineHeight: "1.6",
    textAlign: "center",
    wordBreak: "break-word",

    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      lineHeight: "1.55",
      marginTop: theme.spacing(1),
    },
  },

  notFound: {
    width: "100%",
    maxWidth: 980,
    margin: "0 auto",
    marginTop: theme.spacing(2),
    color: "red",
    fontSize: "16px",
    lineHeight: "1.5",
    textAlign: "center",
    wordBreak: "break-word",

    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
    },
  },

  dashWrapper: {
    width: "100%",
    marginTop: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1.5),
    },
  },
}));

/**
 * Desplaza la ventana hasta el inicio visual del análisis.
 *
 * Se usa scroll manual con offset pequeño para que el usuario vea
 * claramente que empieza una nueva sección (por ejemplo el título
 * y el semáforo), en lugar de dejarla pegada de forma brusca.
 */
function scrollToAnalysisStart(element) {
  if (!element || typeof window === "undefined") return;

  const prefersReducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const rect = element.getBoundingClientRect();
  const currentScroll = window.pageYOffset || document.documentElement.scrollTop || 0;

  /**
   * Dejamos un pequeño margen superior para que el bloque no quede
   * totalmente pegado al borde alto de la pantalla.
   */
  const topOffset = 16;
  const targetTop = Math.max(0, rect.top + currentScroll - topOffset);

  window.scrollTo({
    top: targetTop,
    behavior: prefersReducedMotion ? "auto" : "smooth",
  });
}

const DashInfo = ({ classes }) => {
  const [records, setRecords] = useState([]);

  const { sharedValue } = useGlobal();
  const { showNoResult, packageName, searchedApp } = sharedValue || {};
  const { t } = useTranslation("search");

  const theme = useTheme();

  /**
   * Regla operativa:
   * - activamos el autoscroll solo en pantallas pequeñas
   * - además filtramos por altura para aproximarnos al caso real
   *   de móvil de baja resolución
   */
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isShortViewport =
    typeof window !== "undefined" ? window.innerHeight <= 900 : false;
  const shouldAutoScroll = isSmallScreen && isShortViewport;

  /**
   * Ref al contenedor donde empieza visualmente el dashboard.
   */
  const dashWrapperRef = useRef(null);

  /**
   * Evita repetir el autoscroll varias veces para la misma búsqueda.
   */
  const hasAutoScrolledForCurrentSearchRef = useRef(false);

  /**
   * Mantiene el último package_name observado para reiniciar el guard
   * cuando el usuario hace una nueva búsqueda real.
   */
  const lastPackageNameRef = useRef(null);

  useEffect(() => {
    /**
     * Si cambia la app buscada, reseteamos el guard del autoscroll.
     */
    if (lastPackageNameRef.current !== packageName) {
      hasAutoScrolledForCurrentSearchRef.current = false;
      lastPackageNameRef.current = packageName;
    }

    if (!packageName || showNoResult) {
      setRecords([]);
      return;
    }

    const fetchData = async () => {
      const payload = {
        filtro: { package_name: packageName },
      };

      try {
        const result = await search(payload);
        setRecords(Array.isArray(result) ? result : []);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
        setRecords([]);
      }
    };

    fetchData();
  }, [packageName, showNoResult]);

  useEffect(() => {
    /**
     * Solo hacemos autoscroll si:
     * - estamos en móvil / viewport pequeño
     * - ya hay resultados renderizables
     * - no hay mensaje de "sin resultados"
     * - aún no se ha hecho para esta búsqueda
     */
    if (!shouldAutoScroll) return;
    if (showNoResult) return;
    if (!packageName) return;
    if (records.length === 0) return;
    if (hasAutoScrolledForCurrentSearchRef.current) return;
    if (!dashWrapperRef.current) return;

    /**
     * Esperamos dos frames para asegurarnos de que el bloque
     * ya está en DOM con su altura final antes de desplazar.
     */
    let raf1 = null;
    let raf2 = null;

    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        scrollToAnalysisStart(dashWrapperRef.current);
        hasAutoScrolledForCurrentSearchRef.current = true;
      });
    });

    return () => {
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [records, packageName, showNoResult, shouldAutoScroll]);

  if (records.length > 0) {
    return (
      <div ref={dashWrapperRef} className={classes.dashWrapper}>
        {records.map((record) => (
          <DashView key={record.package_name || record.id} record={record} />
        ))}
      </div>
    );
  }

  if (showNoResult) {
    return (
      <Typography className={classes.notFound}>
        {t("no-result1")} &quot;{searchedApp}&quot; {t("no-result2")}
      </Typography>
    );
  }

  return null;
};

DashInfo.propTypes = {
  classes: PropTypes.shape({
    dashWrapper: PropTypes.string.isRequired,
    notFound: PropTypes.string.isRequired,
  }).isRequired,
};

export default function Search() {
  const classes = useStyles();
  const { t } = useTranslation("search");

  return (
    <GridContainer className={classes.gridContainer}>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary">
            <Typography variant="h5" className={classes.cardTitle}>
              {t("title")}
            </Typography>
          </CardHeader>

          <CardBody className={classes.main}>
            <div className={classes.searchWrapper}>
              <SearchBar />
            </div>

            <Typography className={classes.disclaimer}>
              {t("disclaimer1")}
            </Typography>

            <Typography className={classes.disclaimer}>
              {t("disclaimer2")}
            </Typography>

            <DashInfo classes={classes} />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}